import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useSheetAnim from '../hooks/useSheetAnim';
import { InfiniteSentinel } from './kit';

/**
 * SheetSelect — the app's ONE dropdown. There are no popup dropdowns in this PWA.
 *
 * A native/AntD dropdown is a desktop control: it anchors a floating list to the
 * field, which on a phone lands under the thumb, gets clipped by whatever sheet the
 * form is already inside, and fights the on-screen keyboard. This renders instead as
 * a field-shaped trigger plus a bottom sheet — the options arrive from the bottom
 * edge, at full width, in thumb reach, and the pick is deployed back into the field.
 *
 * The trigger is exactly --field-h tall and borders off the same tokens AntD's
 * inputs do (see theme.js), so a column of SheetSelects, Inputs and DatePickers
 * reads as one control set.
 *
 * Props mirror AntD's Select closely enough that call sites port over as-is:
 *   value / onChange / options / placeholder / disabled / allowClear / loading
 *   options: [{ value, label, sub?, disabled?, keywords? }] — `label` may be a node,
 *     in which case pass `search` or `keywords` for the filter to match on.
 *   showSearch  — filter the given options locally
 *   onSearch(q) — server-backed search; called on open with '' and debounced after
 *   mode="multiple" — checkbox list, value is an array, confirmed with Done
 *   selectedLabel — label for a value whose option isn't in `options` (async search)
 *   footer — node pinned under the list (e.g. a hint)
 *   onLoadMore / hasMore / loadingMore — paged lists (projects, directories): the
 *     sheet drops a sentinel under the last row and calls onLoadMore as it comes
 *     into view, so a long list scrolls in rather than arriving as one capped shot
 *
 * Layering: portaled to <body> at 4900 — above every app sheet (4500/4600) and the
 * offline bar (4000), below the update prompt (5000) and AntD's popup base (6000),
 * so a confirm or toast raised from inside a picker still lands on top. A picker
 * living inside a sheet that itself sits above 4900 (PartyQuickCreate opens at
 * 4950 so it rises over the picker that launched it) must pass a higher `zIndex`,
 * or its options open BEHIND the sheet holding them. Stay under 5000.
 */

const Z = 4900;
const SEARCH_DEBOUNCE = 300;

const Chevron = (p) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
const Tick = (p) => (
  <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" {...p}>
    <polyline points="4 12.5 9.5 18 20 6.5" />
  </svg>
);

// Text to match a typed query against. `label` is often a node (chips, coloured
// "＋ Create new…" rows), so callers can hand us a plain string to search instead.
const haystack = (o) => [o.search, typeof o.label === 'string' ? o.label : '', o.sub, o.keywords]
  .filter(Boolean).join(' ').toLowerCase();

export default function SheetSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select',
  title,
  disabled = false,
  allowClear = false,
  showSearch = false,
  onSearch,
  searchPlaceholder = 'Search…',
  loading = false,
  notFoundContent = 'No options',
  mode,
  selectedLabel,
  footer,
  onClear,
  onOpen,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  style,
  zIndex = Z,
  // Learn Mode anchors. This is the app's ONE picker, so the key always comes
  // from the caller — never hardcode one here.
  tour,
}) {
  const multiple = mode === 'multiple';
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  // Multi-select edits a draft so Cancel can walk away from a half-made change.
  const [draft, setDraft] = useState([]);
  const timer = useRef(null);
  const searchRef = useRef(onSearch);
  searchRef.current = onSearch;

  const selected = multiple ? (Array.isArray(value) ? value : []) : value;
  const selectedOpts = useMemo(
    () => (multiple ? options.filter((o) => selected.includes(o.value)) : []),
    [options, selected, multiple]
  );
  const current = multiple ? null : options.find((o) => o.value === value);
  const hasValue = multiple ? selected.length > 0 : value !== undefined && value !== null && value !== '';

  // Server-side search: prime with '' on open, then debounce each keystroke.
  useEffect(() => {
    if (!open || !searchRef.current) return undefined;
    clearTimeout(timer.current);
    const term = q.trim();
    if (!term) { searchRef.current(''); return undefined; }
    timer.current = setTimeout(() => searchRef.current(term), SEARCH_DEBOUNCE);
    return () => clearTimeout(timer.current);
  }, [q, open]);
  useEffect(() => () => clearTimeout(timer.current), []);

  // Local filtering only — with onSearch the server has already narrowed the list.
  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term || onSearch || !showSearch) return options;
    return options.filter((o) => haystack(o).includes(term));
  }, [options, q, onSearch, showSearch]);

  const searchable = showSearch || !!onSearch;

  const openSheet = () => {
    if (disabled) return;
    setQ('');
    setDraft(multiple ? [...selected] : []);
    setOpen(true);
    onOpen?.();
  };
  const close = () => setOpen(false);

  const pick = (o) => {
    if (o.disabled) return;
    if (multiple) {
      setDraft((xs) => (xs.includes(o.value) ? xs.filter((x) => x !== o.value) : [...xs, o.value]));
      return;
    }
    close();
    onChange?.(o.value, o);
  };
  const confirmMultiple = () => { close(); onChange?.(draft); };
  const clear = (e) => {
    e?.stopPropagation();
    onChange?.(multiple ? [] : undefined);
    onClear?.();
  };

  // What the closed field shows. Async-search pickers hold a value whose option
  // has since been searched away, hence the selectedLabel fallback.
  let display = null;
  if (multiple && selected.length) {
    display = selectedOpts.length
      ? selectedOpts.map((o) => (typeof o.label === 'string' ? o.label : o.value)).join(', ')
      : `${selected.length} selected`;
  } else if (!multiple && hasValue) {
    display = current ? current.label : (selectedLabel ?? String(value));
  }

  return (
    <>
      <div
        data-tour={tour}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-disabled={disabled || undefined}
        onClick={openSheet}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSheet(); } }}
        style={{
          height: 'var(--field-h)',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0 12px',
          borderRadius: 12,
          border: '1px solid var(--border)',
          background: disabled ? 'var(--surface2)' : 'var(--surface)',
          color: display ? 'var(--text)' : 'var(--muted)',
          fontSize: 14,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.65 : 1,
          userSelect: 'none',
          ...style,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {display || placeholder}
        </span>
        {allowClear && hasValue && !disabled && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear"
            onClick={clear}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); clear(e); } }}
            style={{ flex: 'none', width: 18, height: 18, borderRadius: 50, background: 'var(--chip)', color: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, lineHeight: 1 }}
          >
            ✕
          </span>
        )}
        <span style={{ flex: 'none', color: 'var(--muted)', display: 'flex' }}><Chevron /></span>
      </div>

      <OptionSheet
        open={open}
        title={title || placeholder}
        onClose={close}
        searchable={searchable}
        searchPlaceholder={searchPlaceholder}
        q={q}
        setQ={setQ}
        loading={loading}
        options={shown}
        notFoundContent={notFoundContent}
        multiple={multiple}
        selected={multiple ? draft : selected}
        onPick={pick}
        onConfirm={confirmMultiple}
        allowClear={allowClear && hasValue}
        onClearAll={() => { clear(); close(); }}
        footer={footer}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        loadingMore={loadingMore}
        zIndex={zIndex}
      />
    </>
  );
}

/**
 * The sheet itself, exported so pickers with bespoke rows (a listing card, a
 * contact with a phone number) can reuse the chrome without reimplementing the
 * scrim, the exit animation, the search box and the empty states.
 */
export function OptionSheet({
  open, title, onClose, searchable, searchPlaceholder, q, setQ, loading,
  options = [], notFoundContent, multiple, selected = [], onPick, onConfirm,
  allowClear, onClearAll, footer, children, zIndex = Z,
  onLoadMore, hasMore = false, loadingMore = false, autoFocusSearch = true,
}) {
  const { mounted, closing } = useSheetAnim(open);
  const inputRef = useRef(null);

  // Focus the search box a beat after the rise, so the keyboard doesn't shove the
  // panel around mid-animation. A picker you open in order to TYPE wants this; one
  // you open in order to BROWSE (a short grouped list) must opt out, or the keyboard
  // covers the very list it was meant to show.
  useEffect(() => {
    if (!open || !searchable || !autoFocusSearch) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 260);
    return () => clearTimeout(t);
  }, [open, searchable, autoFocusSearch]);

  // Hardware/browser back closes the picker instead of leaving the page.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!mounted) return null;

  const isSelected = (v) => (Array.isArray(selected) ? selected.includes(v) : selected === v);

  return createPortal(
    <div
      onClick={onClose}
      className={`sheet-scrim${closing ? ' is-closing' : ''}`}
      style={{ position: 'fixed', inset: 0, zIndex, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`sheet-panel${closing ? ' is-closing' : ''}`}
        style={{ background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '86%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        {/* grab handle — reads as draggable even though tapping the scrim is the gesture */}
        <div style={{ padding: '8px 0 2px', display: 'flex', justifyContent: 'center', flex: 'none' }}>
          <span style={{ width: 38, height: 4, borderRadius: 2, background: 'var(--border)' }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 16px 10px', borderBottom: '1px solid var(--border)', flex: 'none' }}>
          <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
          {multiple && (
            <span onClick={onConfirm} style={{ flex: 'none', fontSize: 13, fontWeight: 600, color: 'var(--primary)', cursor: 'pointer', padding: '4px 6px' }}>Done</span>
          )}
          <div
            onClick={onClose}
            role="button"
            tabIndex={0}
            aria-label="Close"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose?.(); } }}
            style={{ width: 32, height: 32, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)', flex: 'none' }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="5" y1="5" x2="19" y2="19" /><line x1="19" y1="5" x2="5" y2="19" /></svg>
          </div>
        </div>

        {searchable && (
          <div style={{ padding: '10px 16px 4px', flex: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 'var(--field-h)', padding: '0 12px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12 }}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="var(--muted)" strokeWidth="2" strokeLinecap="round" style={{ flex: 'none' }}>
                <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" />
              </svg>
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={searchPlaceholder}
                inputMode="search"
                style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: 'var(--text)' }}
              />
              {q && (
                <span onClick={() => setQ('')} style={{ flex: 'none', width: 18, height: 18, borderRadius: 50, background: 'var(--chip)', color: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, cursor: 'pointer', lineHeight: 1 }}>✕</span>
              )}
            </div>
          </div>
        )}

        {/* 4px of side padding + 12px inside each row lines option text up with the
            search box above it; the padding is on the scroller so the row highlight
            still bleeds wider than the text. */}
        <div className="scrx" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '6px 4px 8px', WebkitOverflowScrolling: 'touch' }}>
          {children ?? (
            loading ? (
              <div style={{ padding: '28px 12px', textAlign: 'center', fontSize: 13, color: 'var(--muted)' }}>Loading…</div>
            ) : options.length === 0 ? (
              <div style={{ padding: '30px 20px', textAlign: 'center', fontSize: 13, color: 'var(--muted)', lineHeight: 1.55 }}>{notFoundContent}</div>
            ) : (
              <>
              {options.map((o) => {
                const on = isSelected(o.value);
                return (
                  <div
                    key={String(o.value)}
                    data-tour={o.tour}
                    role="option"
                    aria-selected={on}
                    onClick={() => onPick?.(o)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      minHeight: 48, padding: '14px 15px', borderRadius: 12,
                      cursor: o.disabled ? 'not-allowed' : 'pointer',
                      opacity: o.disabled ? 0.45 : 1,
                      background: on ? 'var(--chip)' : 'transparent',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13.5, fontWeight: on ? 600 : 500, color: on ? 'var(--primary)' : 'var(--text)', lineHeight: 1.35 }}>{o.label}</div>
                      {o.sub && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2, lineHeight: 1.35 }}>{o.sub}</div>}
                    </div>
                    {on && <span style={{ flex: 'none', color: 'var(--primary)', display: 'flex' }}><Tick /></span>}
                  </div>
                );
              })}
              {onLoadMore && (
                <InfiniteSentinel hasMore={hasMore} loading={loadingMore} onReach={onLoadMore} />
              )}
              </>
            )
          )}
        </div>

        {(footer || allowClear) && (
          <div style={{ padding: '10px 16px calc(10px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', flex: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {footer}
            {allowClear && (
              <span onClick={onClearAll} style={{ fontSize: 13, fontWeight: 600, color: 'var(--bad)', cursor: 'pointer', textAlign: 'center', padding: '4px 0' }}>Clear selection</span>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
